import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { MdDelete, MdEdit } from 'react-icons/md';

import Layout from '../Layout';
import {
  creerPoste, lireChiffrage, listerPostes, modifierPoste, supprimerPoste,
} from '../api';

const euros = (v) =>
  v === null || v === undefined
    ? '—'
    : `${Number(v).toLocaleString('fr-BE', { minimumFractionDigits: 2,
        maximumFractionDigits: 2 })} €`;

/**
 * Les frais qui ne viennent d'aucun barème.
 *
 * Un relogement ne se chiffre pas au mètre carré : il se réclame dans la
 * limite prévue à la police. Les codes sont ceux de `PostCostings.origin`,
 * qui les prévoyait déjà — seul le parcours assuré les ignorait.
 */
const FRAIS = [
  { origin: '5', titre: 'Frais de relogement',
    aide: "Hôtel, location, garde-meuble, le temps que le logement redevienne habitable.",
    limite: "Votre contrat fixe presque toujours une durée et un plafond." },
  { origin: '7', titre: "Honoraires d'expert conseil",
    aide: "L'expert que vous mandatez pour vous défendre, distinct de celui de la compagnie.",
    limite: "Souvent pris en charge, dans une limite exprimée en pourcentage de l'indemnité." },
  { origin: '2', titre: 'Pertes indirectes',
    aide: "Denrées congelées perdues, vêtements, frais de nettoyage.",
    limite: "Souvent un forfait, parfois un pourcentage du dommage." },
  { origin: '9', titre: 'Recherche de fuite et remise en état',
    aide: "Le sondage, la démolition pour atteindre la fuite, et la réfection après.",
    limite: "Généralement couverte même si la conduite elle-même ne l'est pas." },
  { origin: '6', titre: "Perte d'usage ou de loyers",
    aide: "Si vous louez le bien, ou si une partie devient inutilisable.",
    limite: "Limitée à une durée prévue au contrat." },
  { origin: '8', titre: 'Frais administratifs',
    aide: "Déplacements, courriers, copies, temps passé.",
    limite: '' },
  { origin: '9', titre: 'Autres frais', aide: '', limite: '' },
];

const VIDE = { title: '', origin: '5', reclamer: '', comment: '' };

function Formulaire({ ouvert, valeurs, onChange, onFermer, onEnregistrer,
                     occupe, erreur }) {
  const modele = FRAIS.find((f) => f.titre === valeurs.title);
  return (
    <Dialog open={ouvert} onClose={onFermer} fullWidth maxWidth="sm">
      <DialogTitle>{valeurs.id ? 'Modifier un frais' : 'Ajouter un frais'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {erreur && <Alert severity="error">{erreur}</Alert>}

          <TextField
            select label="De quel frais s'agit-il ?" value={valeurs.title}
            onChange={(e) => {
              const choisi = FRAIS.find((f) => f.titre === e.target.value);
              onChange({ ...valeurs, title: e.target.value,
                         origin: choisi?.origin ?? valeurs.origin });
            }}
            helperText={modele?.aide || ' '}
          >
            {FRAIS.map((f) => (
              <MenuItem key={f.titre} value={f.titre}>{f.titre}</MenuItem>
            ))}
          </TextField>

          {modele?.limite && <Alert severity="info">{modele.limite}</Alert>}

          <TextField
            label="Montant que vous réclamez" type="number"
            value={valeurs.reclamer}
            onChange={(e) => onChange({ ...valeurs, reclamer: e.target.value })}
            InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
            helperText="Hors TVA. Appuyez-le sur une facture ou un devis quand vous en avez un."
          />

          <TextField
            label="Précisions" multiline minRows={2} value={valeurs.comment}
            onChange={(e) => onChange({ ...valeurs, comment: e.target.value })}
            helperText="Période, nombre de nuits, qui a été mandaté…"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onFermer}>Annuler</Button>
        <Button variant="contained" onClick={onEnregistrer}
                disabled={occupe || !valeurs.title}>
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Sixième étape : ce que le sinistre coûte en dehors des travaux. */
export default function FraisAnnexes({ expertiseId, onSuivant, onRetour,
                                       onEtape }) {
  const [postes, setPostes] = useState(null);
  const [chiffrageId, setChiffrageId] = useState(null);
  const [erreur, setErreur] = useState('');
  const [erreurForm, setErreurForm] = useState('');
  const [edition, setEdition] = useState(null);
  const [occupe, setOccupe] = useState(false);

  const recharger = useCallback(() => {
    Promise.all([listerPostes(expertiseId), lireChiffrage(expertiseId)])
      .then(([liste, chiffrage]) => {
        // Les postes issus d'un dommage relèvent de l'étape chiffrage.
        setPostes(liste.filter((p) => !p.dommage && p.origin !== '1'));
        setChiffrageId(chiffrage?.id ?? null);
      })
      .catch(() => setErreur('Impossible de charger vos frais.'));
  }, [expertiseId]);

  useEffect(() => { recharger(); }, [recharger]);

  const enregistrer = async () => {
    setOccupe(true);
    setErreurForm('');
    try {
      const champs = {
        title: edition.title, origin: edition.origin,
        comment: edition.comment || '',
        reclamer: edition.reclamer === '' ? 0 : edition.reclamer,
      };
      if (edition.id) await modifierPoste(edition.id, champs);
      else await creerPoste(expertiseId, chiffrageId, champs);
      setEdition(null);
      recharger();
    } catch {
      setErreurForm("L'enregistrement n'a pas abouti. Réessayez.");
    } finally {
      setOccupe(false);
    }
  };

  const total = (postes || []).reduce(
    (somme, p) => somme + Number(p.reclamer || 0), 0);

  return (
    <Layout
      etape="frais"
      titre="Vos autres frais"
      introduction="Un sinistre coûte au-delà des travaux : se reloger, se faire assister, remplacer ce qui a été perdu. Ces postes ne viennent d'aucun barème — ils se réclament dans les limites de votre contrat."
      onEtape={onEtape}
      actions={
        <>
          <Button onClick={onRetour}>Retour</Button>
          <Button variant="contained" size="large" onClick={onSuivant}>
            Continuer
          </Button>
        </>
      }
    >
      {erreur && <Alert severity="error" sx={{ mb: 3 }}>{erreur}</Alert>}
      {postes === null && !erreur && <CircularProgress size={28} />}

      <Stack spacing={2}>
        {postes?.map((p) => (
          <Paper key={p.id} variant="outlined" sx={{ p: 2.5 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {p.title}
                </Typography>
                {p.comment && (
                  <Typography variant="body2" color="text.secondary">
                    {p.comment}
                  </Typography>
                )}
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600,
                                                whiteSpace: 'nowrap' }}>
                {euros(p.reclamer)}
              </Typography>
              <IconButton aria-label={`Modifier ${p.title}`}
                          onClick={() => setEdition({
                            ...VIDE, ...p, reclamer: p.reclamer ?? '',
                            comment: p.comment ?? '' })}>
                <MdEdit />
              </IconButton>
              <IconButton aria-label={`Supprimer ${p.title}`}
                          onClick={async () => {
                            await supprimerPoste(p.id); recharger();
                          }}>
                <MdDelete />
              </IconButton>
            </Stack>
          </Paper>
        ))}

        {postes?.length === 0 && (
          <Alert severity="info">
            Aucun frais annexe déclaré. Si le sinistre ne vous a coûté que des
            travaux, passez à la suite.
          </Alert>
        )}

        {postes?.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body1">Total de vos autres frais</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {euros(total)}
              </Typography>
            </Stack>
          </Paper>
        )}

        <Paper variant="outlined"
               sx={{ p: 3, borderStyle: 'dashed', textAlign: 'center' }}>
          <Button variant="contained" disabled={!chiffrageId}
                  onClick={() => { setErreurForm(''); setEdition({ ...VIDE }); }}>
            Ajouter un frais
          </Button>
        </Paper>

        <Alert severity="info">
          Ces postes se réclament dans les limites de votre contrat, souvent
          exprimées en durée ou en pourcentage. L'analyse de vos conditions
          dira lesquelles s'appliquent — déclarez d'abord ce que vous avez
          réellement dépensé.
        </Alert>
      </Stack>

      <Formulaire
        ouvert={edition !== null}
        valeurs={edition || VIDE}
        onChange={setEdition}
        onFermer={() => setEdition(null)}
        onEnregistrer={enregistrer}
        occupe={occupe}
        erreur={erreurForm}
      />
    </Layout>
  );
}
